import { FeatureKeys, ModularProductTier, UserProfiles } from '@apis/common';
import type { Keys } from '@hooks/useFeatures';
import { useEnabledFeatures } from '@hooks/useFeatures';
import userAtom from '@stateAtoms/userAtom';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { camelCase, titleCase } from 'tiny-case';
import { CamelCase } from 'type-fest';

export const showPagesFeatureKeys: Keys[] = [
    'show_menu_phone_report',
    'show_menu_cases',
    'show_menu_certified_authentications'
];

export default function useUserPermission() {
    const user = useAtomValue(userAtom);
    const [enabledFeatures, loading, get] = useEnabledFeatures([
        ...showPagesFeatureKeys,
    ]);

    const profileIsAllowed = useCallback(
        (acceptedProfiles?: UserProfiles[]) =>
            !user ||
            !acceptedProfiles ||
            acceptedProfiles?.includes(user?.profile),
        [user?.profile],
    );

    const userFeatureIsAllowed = useCallback(
        (requiredFeatures?: FeatureKeys[]) =>
            !user ||
            !requiredFeatures ||
            requiredFeatures.some((x) => user.features?.includes(x)),
        [user?.features],
    );

    const modularProductIsAllowed = useCallback(
        (requiredTiers?: ModularProductTier[]) =>
            !requiredTiers ||
            requiredTiers.length === 0 ||
            requiredTiers.includes(user?.modularProductTier ?? ModularProductTier.FraudShield),
        [user?.modularProductTier],
    );

    const isAllowedInFeatureToggle = useCallback(
        (path?: string) => {
            if (!path) return false;

            const feature =
                enabledFeatures?.[
                    `showMenu${titleCase(path).replace(
                        /\s/g,
                        '',
                    )}` as CamelCase<Keys>
                ];

            return feature === undefined || feature;
        },
        [JSON.stringify(enabledFeatures)],
    );

    const isAllowed = useCallback(
        (
            path?: string,
            acceptedProfiles?: UserProfiles[],
            requiredFeatures?: FeatureKeys[],
            requiredModularProduct?: ModularProductTier[],
        ) =>
            isAllowedInFeatureToggle(path) &&
            profileIsAllowed(acceptedProfiles) &&
            userFeatureIsAllowed(requiredFeatures) &&
            modularProductIsAllowed(requiredModularProduct),
        [isAllowedInFeatureToggle, profileIsAllowed, userFeatureIsAllowed, modularProductIsAllowed],
    );

    const featuresLoadedAndIsAllowedAsync = useCallback(
        async (
            path?: string,
            acceptedProfiles?: UserProfiles[],
            requiredFeature?: FeatureKeys[],
            requiredModularProduct?: ModularProductTier[],
        ) => {
            if (!path) return false;

            if ((showPagesFeatureKeys as string[]).includes(camelCase(path))) {
                // eslint-disable-next-line @typescript-eslint/await-thenable
                await get();
            }
            if (path?.includes('blocklist')) return false;

            return isAllowed(path, acceptedProfiles, requiredFeature, requiredModularProduct);
        },
        [isAllowed],
    );

    return useMemo(
        () => ({
            profileIsAllowed,
            userFeatureIsAllowed,
            modularProductIsAllowed,
            isAllowedInFeatureToggle,
            isAllowed,
            featuresLoadedAndIsAllowedAsync,
            loading,
        }),
        [
            profileIsAllowed,
            userFeatureIsAllowed,
            modularProductIsAllowed,
            isAllowedInFeatureToggle,
            isAllowed,
            featuresLoadedAndIsAllowedAsync,
            loading,
        ],
    );
}
